import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon, PlusIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../lib/supabase';
import { useGuideTimezone } from '../hooks/useGuideTimezone';
import {
  SeekerDetailModalProps,
  Session,
  Note,
  Seeker,
  SessionStatus,
  OfferStatus,
  PackageOffer,
  PackageOfferUnit,
  OfferUnitStatus,
} from './SeekerDetailModal/types';
import { generateAvatar, formatCurrency } from './SeekerDetailModal/utils';
import SeekerIntakeModal from './SeekerIntakeModal';
import GuideChat from './GuideChat';
import SendPackageModal from './SendPackageModal';
import type { Package } from './packages/PackageList';
import ScheduleUnitModal from './ScheduleUnitModal';
import ScheduleSessionModal from './ScheduleSessionModal';
import BatchScheduleModal from './BatchScheduleModal';

type MatchesRow = {
  id: string;
  seeker_id: string;
  guide_id: string;
  match_score: number | null;
  reasoning: string[] | null;
  score_breakdown: Record<string, any> | null;
  created_at: string | null;
};

type ScheduledSessionRow = {
  id: string;
  session_type: string | null;
  session_status: string | null;
  session_date: string | null;
  session_time: string | null;
  start_time?: string | null; // UTC timestamp
  end_time?: string | null; // UTC timestamp
  guide_timezone?: string | null; // IANA timezone ID
  created_at: string | null;
  duration_minutes: number | null;
  scheduled_by?: 'guide' | 'seeker' | null; // Who scheduled/rescheduled
};

type NoteRow = {
  id: string;
  note_text: string | null;
  created_at: string | null;
};

type PackageOfferUnitRow = {
  id: number;
  template_component_id: number | null;
  component_name: string;
  component_type: string;
  unit_index: number;
  status: string;
  unit_price_cents: number | null;
  scheduled_session_id: string | null;
  notes: string | null;
  session_length_minutes: number | null;
};

type PackageOfferRow = {
  id: string;
  name: string;
  description: string | null;
  duration_months: number | null;
  status: string;
  subtotal_cents: number | null;
  total_cents: number | null;
  created_at: string | null;
  package_offer_units?: PackageOfferUnitRow[] | null;
};

const SESSION_TYPE_LABEL: Record<string, string> = {
  consultation: 'Consultation',
  preparation: 'Preparation Session',
  integration: 'Integration Session',
  journey: 'Journey Session',
  follow_up: 'Follow Up',
};

const SESSION_STATUS_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No Show',
  pending: 'Pending',
};

const OFFER_STATUS_BADGE_CLASS: Record<OfferStatus, string> = {
  draft: 'border border-[#2C2731]/40 bg-[#E8DFBD] text-[#2C2731]',
  sent: 'border border-[#2C2731] bg-[#FFFCEF] text-[#2C2731]',
  accepted: 'border border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]',
  rejected: 'border border-[#2C2731]/40 bg-[#F3EBCD] text-[#2C2731]',
  withdrawn: 'border border-[#2C2731]/30 bg-[#2C2731]/10 text-[#2C2731]',
};

const UNIT_STATUS_CLASSES: Record<OfferUnitStatus, string> = {
  unallocated: 'border border-[#2C2731]/40 bg-[#E8DFBD] text-[#2C2731]',
  scheduled: 'border border-[#2C2731] bg-[#FFFCEF] text-[#2C2731]',
  fulfilled: 'border border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]',
  cancelled: 'border border-[#2C2731]/40 bg-[#2C2731]/10 text-[#2C2731]',
};

const COMPONENT_TYPE_DEFAULT_DURATIONS: Record<string, number> = {
  consultation: 20,
  preparation: 60,
  integration: 60,
  ceremony: 60,
  journey: 60,
  follow_up: 60,
  additional: 60,
};

const parseDurationFromNotes = (notes: string | null | undefined): number | null => {
  if (!notes) return null;
  const trimmed = notes.trim();
  if (!trimmed) return null;

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      const candidate = (parsed as Record<string, unknown>)['duration_minutes']
        ?? (parsed as Record<string, unknown>)['durationMinutes']
        ?? (parsed as Record<string, unknown>)['duration'];
      if (typeof candidate === 'number' && candidate > 0) {
        return candidate;
      }
      if (typeof candidate === 'string') {
        const numericValue = Number.parseInt(candidate, 10);
        if (!Number.isNaN(numericValue) && numericValue > 0) {
          return numericValue;
        }
      }
    }
  } catch {
    // Not JSON, fall through to regex parsing
  }

  const match = trimmed.match(/(\d+)\s*(?:min|mins|minutes|minute)/i);
  if (match) {
    const numericValue = Number.parseInt(match[1], 10);
    if (!Number.isNaN(numericValue) && numericValue > 0) {
      return numericValue;
    }
  }

  return null;
};

const parseDurationFromName = (name: string | null | undefined): number | null => {
  if (!name) return null;
  const match = name.match(/(\d+)\s*(?:min|mins|minutes|minute)/i);
  if (!match) return null;
  const numericValue = Number.parseInt(match[1], 10);
  if (Number.isNaN(numericValue) || numericValue <= 0) {
    return null;
  }
  return numericValue;
};

const deriveUnitDuration = (unit: PackageOfferUnitRow): number | null => {
  const fromNotes = parseDurationFromNotes(unit.notes);
  if (fromNotes) return fromNotes;

  const fromName = parseDurationFromName(unit.component_name);
  if (fromName) return fromName;

  const componentKey = typeof unit.component_type === 'string' ? unit.component_type.toLowerCase() : '';
  const defaultDuration = COMPONENT_TYPE_DEFAULT_DURATIONS[componentKey];
  return typeof defaultDuration === 'number' ? defaultDuration : null;
};

const formatDateTimeLabel = (
  startUtc?: string | null,
  sessionTimezone?: string | null,
  fallbackTimezone?: string | null,
  durationMinutes?: number | null
) => {
  // ONLY use start_time (UTC) and convert to guide's timezone
  if (!startUtc) {
    return undefined;
  }

  // Use session's timezone, or fallback to guide's current timezone from basic_info
  const guideTimezone = sessionTimezone || fallbackTimezone;

  if (!guideTimezone) {
    return undefined;
  }

  try {
    const formattedDateTime = new Intl.DateTimeFormat('en-US', {
      timeZone: guideTimezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'shortGeneric',
    }).format(new Date(startUtc));

    // Add duration if available
    if (durationMinutes && durationMinutes > 0) {
      return `${formattedDateTime} (${durationMinutes} min)`;
    }

    return formattedDateTime;
  } catch (error) {
    console.error('Error formatting date in guide timezone:', error);
    return undefined;
  }
};

const mapSessionRow = (row: ScheduledSessionRow, fallbackGuideTimezone?: string | null): Session => {
  const typeKey = row.session_type?.toLowerCase() ?? 'consultation';
  const sessionType = (['consultation', 'preparation', 'integration', 'journey', 'follow_up'] as const).includes(typeKey as any)
    ? (typeKey as Session['type'])
    : 'other';

  const statusKey = row.session_status?.toLowerCase() ?? 'scheduled';
  const status = (['scheduled', 'confirmed', 'completed', 'cancelled', 'no_show'] as const).includes(statusKey as any)
    ? (statusKey as Session['status'])
    : 'pending';

  return {
    id: row.id,
    name: SESSION_TYPE_LABEL[typeKey] ?? 'Session',
    type: sessionType,
    status,
    startsAt: row.start_time ?? undefined, // ONLY use start_time (UTC)
    dateLabel: formatDateTimeLabel(
      row.start_time ?? undefined,
      row.guide_timezone ?? undefined,
      fallbackGuideTimezone,
      row.duration_minutes ?? null
    ),
    durationMinutes: row.duration_minutes ?? null,
    guideTimezone: row.guide_timezone ?? fallbackGuideTimezone ?? undefined,
    scheduledBy: row.scheduled_by ?? null,
  };
};

const mapNotes = (rows: NoteRow[] | null | undefined): Note[] => {
  if (!rows || rows.length === 0) return [];
  return rows
    .map((row) => ({
      id: row.id,
      date: row.created_at ? new Date(row.created_at).toLocaleString() : 'Recently',
      content: row.note_text ?? '',
    }))
    .filter((note) => note.content.trim().length > 0);
};

const mapPackageOffer = (
  offerRow: PackageOfferRow | null,
  units: PackageOfferUnitRow[] | null | undefined,
): PackageOffer | null => {
  if (!offerRow) return null;

  const mappedUnits: PackageOfferUnit[] = (units ?? []).map((unit) => ({
    id: unit.id,
    templateComponentId: unit.template_component_id ?? undefined,
    componentName: unit.component_name,
    componentType: unit.component_type ?? 'additional',
    unitIndex: unit.unit_index,
    status: (unit.status as PackageOfferUnit['status']) ?? 'unallocated',
    unitPriceCents: unit.unit_price_cents ?? 0,
    scheduledSessionId: unit.scheduled_session_id ?? null,
    notes: unit.notes ?? null,
    durationMinutes: unit.session_length_minutes ?? deriveUnitDuration(unit),
  }));

  return {
    id: offerRow.id,
    name: offerRow.name,
    description: offerRow.description,
    durationMonths: offerRow.duration_months,
    status: offerRow.status as PackageOffer['status'],
    subtotalCents: offerRow.subtotal_cents ?? 0,
    totalCents: offerRow.total_cents ?? offerRow.subtotal_cents ?? 0,
    createdAt: offerRow.created_at ?? new Date().toISOString(),
    units: mappedUnits,
  };
};

const computeStats = (sessions: Session[]): Seeker['stats'] => {
  if (sessions.length === 0) {
    return {
      totalSessions: 0,
      completedSessions: 0,
      lastSessionLabel: 'No sessions yet',
    };
  }

  const completed = sessions.filter((session) => session.status === 'completed');
  const lastSession = [...sessions]
    .sort((a, b) => (a.startsAt ?? '').localeCompare(b.startsAt ?? ''))
    .reverse()
    .find((session) => !!session.dateLabel);

  return {
    totalSessions: sessions.length,
    completedSessions: completed.length,
    lastSessionLabel: lastSession?.dateLabel ?? 'No sessions yet',
  };
};

const formatDisplayLabel = (value: string): string =>
  value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const GUIDE_CHANGE_REASONS = [
  { id: 'seeker_request', label: 'Seeker requested a change' },
  { id: 'availability_conflict', label: 'Schedule conflicts' },
  { id: 'needs_different_support', label: 'Needs different expertise/support' },
  { id: 'alignment_concerns', label: 'Alignment or safety concerns' },
  { id: 'other', label: 'Other reason' }
];

const buildSeekerDetail = (
  seekerId: string,
  baseName: string,
  _email: string | null | undefined, // Unused — seeker email must never be exposed to guides
  phone: string | null | undefined,
  matchRow: MatchesRow | null,
  sessions: Session[],
  notes: Note[],
  packageOffers: PackageOffer[],
): Seeker => {
  const upcomingSession = sessions.find((session) => ['scheduled', 'confirmed'].includes(session.status))
    ?? sessions.find((session) => session.status !== 'completed' && session.status !== 'cancelled' && session.status !== 'no_show')
    ?? null;

  const stats = computeStats(sessions);

  const packageNameFromMatch = matchRow?.score_breakdown && typeof matchRow.score_breakdown === 'object'
    ? matchRow.score_breakdown['package_name'] ?? null
    : null;

  const packagePriceFromMatch = matchRow?.score_breakdown && typeof matchRow.score_breakdown === 'object'
    ? matchRow.score_breakdown['package_price'] ?? null
    : null;

  // Get most recent package offer for legacy compatibility
  const latestOffer = packageOffers.length > 0 ? packageOffers[0] : null;
  const derivedPackageName = latestOffer?.name ?? packageNameFromMatch ?? null;
  const derivedPackagePrice = latestOffer
    ? latestOffer.totalCents / 100
    : packagePriceFromMatch
      ? Number(packagePriceFromMatch)
      : null;

  return {
    id: seekerId,
    name: baseName,
    activeDate: matchRow?.created_at ? `Matched on ${new Date(matchRow.created_at).toLocaleDateString()}` : undefined,
    packageName: derivedPackageName,
    packagePrice: derivedPackagePrice,
    packageOffer: latestOffer, // Legacy - most recent offer
    packageOffers, // New - all offers
    matchScore: matchRow?.match_score ?? null,
    matchReasoning: matchRow?.reasoning ?? null,
    sessions,
    upcomingSession,
    notes,
    stats,
    phone: phone ?? undefined,
  };
};

// Session management functions
const updateSessionStatus = async (sessionId: string, status: SessionStatus): Promise<void> => {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from('scheduled_sessions')
    .update({ 
      session_status: status,
      updated_at: new Date().toISOString()
    })
    .eq('id', sessionId);
  
  if (error) {
    throw error;
  }
};

const rescheduleSession = async (sessionId: string, newDate: string, newTime: string, startUtc?: string): Promise<void> => {
  const supabase = getSupabaseClient();

  // Use the session-reschedule edge function instead of direct database update
  // This ensures proper timezone conversion, notifications, and calendar updates
  const { data, error } = await supabase.functions.invoke('session-reschedule', {
    body: {
      session_id: sessionId,
      new_date: newDate,
      new_time: `${newTime}:00`, // Edge function expects HH:MM:SS format
      start_utc: startUtc || undefined,
    },
  });

  if (error) {
    throw new Error(error.message || 'Failed to reschedule session');
  }
};

const canFitSession = (
  timeSlots: Array<{ time: string; available: boolean }>,
  startTime: string,
  durationMinutes: number
): boolean => {
  // Find the index of the start time
  const startIndex = timeSlots.findIndex(slot => slot.time === startTime);
  if (startIndex === -1 || !timeSlots[startIndex].available) {
    return false;
  }

  // Calculate how many 15-minute slots we need (assuming slots are 15 minutes apart)
  const slotsNeeded = Math.ceil(durationMinutes / 15);

  // Check if we have enough consecutive available slots
  for (let i = 0; i < slotsNeeded; i++) {
    const slotIndex = startIndex + i;
    if (slotIndex >= timeSlots.length || !timeSlots[slotIndex].available) {
      return false;
    }
  }

  return true;
};

const SeekerDetailModal: React.FC<SeekerDetailModalProps> = ({
  isOpen,
  onClose,
  seeker,
  onDataChange,
}) => {
  const [detail, setDetail] = useState<Seeker | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleStartUtc, setRescheduleStartUtc] = useState<string>(''); // Track selected UTC time
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [guideId, setGuideId] = useState<string | null>(null);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [seekerUserId, setSeekerUserId] = useState<string | null>(null);
  const [seekerHasAccount, setSeekerHasAccount] = useState<boolean>(false);
  const [showChat, setShowChat] = useState(false);
  const [isChatDrawerVisible, setIsChatDrawerVisible] = useState(false);
  const [showSendPackageModal, setShowSendPackageModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showScheduleUnitModal, setShowScheduleUnitModal] = useState(false);
  const [unitToSchedule, setUnitToSchedule] = useState<PackageOfferUnit | null>(null);
  const [scheduleModalSeekerId, setScheduleModalSeekerId] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [latestMatch, setLatestMatch] = useState<MatchesRow | null>(null);
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [changeDetails, setChangeDetails] = useState('');
  const [changeError, setChangeError] = useState<string | null>(null);
  const [isSubmittingChange, setIsSubmittingChange] = useState(false);
  const [rescheduleAvailability, setRescheduleAvailability] = useState<Array<{
    date: string;
    timeSlots: Array<{
      time: string;
      available: boolean;
      start_utc?: string;
      end_utc?: string;
      guide_timezone?: string;
    }>;
  }>>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [sessionToMarkNoShow, setSessionToMarkNoShow] = useState<Session | null>(null);
  const [noShowNotes, setNoShowNotes] = useState('');
  const [guideInitiatedChanges, setGuideInitiatedChanges] = useState<number>(0);
  const [showScheduleSessionModal, setShowScheduleSessionModal] = useState(false);
  const [showBatchScheduleModal, setShowBatchScheduleModal] = useState(false);
  const [packageReviews, setPackageReviews] = useState<Record<string, any>>({});
  const [rescheduleDisplayedMonth, setRescheduleDisplayedMonth] = useState<Date>(() => new Date());
  const [rescindingOfferId, setRescindingOfferId] = useState<string | null>(null);
  const [showRescindConfirm, setShowRescindConfirm] = useState<string | null>(null);
  const closeChatTimeoutRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const { timezone: guideTimezone } = useGuideTimezone();

  const computeUpcomingSession = useCallback((sessions: Session[]): Session | null => {
    return sessions.find((session) => session.status === 'scheduled' || session.status === 'confirmed')
      ?? sessions.find((session) => session.status !== 'completed' && session.status !== 'cancelled' && session.status !== 'no_show')
      ?? null;
  }, []);

  const patchSession = useCallback((sessionId: string, updates: Partial<Session>) => {
    setDetail((prev) => {
      if (!prev) {
        return prev;
      }
      const updatedSessions = prev.sessions.map((session) =>
        session.id === sessionId ? { ...session, ...updates } : session
      );
      return {
        ...prev,
        sessions: updatedSessions,
        upcomingSession: computeUpcomingSession(updatedSessions),
      };
    });
  }, [computeUpcomingSession]);

  const upcomingSessions = useMemo(() => {
    const sourceSessions = detail?.sessions ?? [];
    const fallbackSessions =
      sourceSessions.length === 0 && detail?.upcomingSession ? [detail.upcomingSession] : sourceSessions;

    const relevantSessions = fallbackSessions.filter(
      (session) => !['completed', 'cancelled', 'no_show'].includes(session.status)
    );

    const getTimestamp = (value?: string): number => {
      if (!value) return Number.MAX_SAFE_INTEGER;
      const parsed = new Date(value);
      const ms = parsed.getTime();
      return Number.isNaN(ms) ? Number.MAX_SAFE_INTEGER : ms;
    };

    return relevantSessions.sort((a, b) => {
      const diff = getTimestamp(a.startsAt) - getTimestamp(b.startsAt);
      if (diff !== 0) {
        return diff;
      }
      return a.name.localeCompare(b.name);
    });
  }, [detail?.sessions, detail?.upcomingSession]);

  const handleJoinWaitingRoom = useCallback((session: Session) => {
    if (!session?.id) return;
    navigate(`/waiting-room?sessionId=${session.id}`);
  }, [navigate]);

  const handleCompleteSession = useCallback(async (session: Session) => {
    if (!session?.id) return;
    setActionLoading(`complete-${session.id}`);
    setSuccessMessage(null);
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { data: { session: authSession } } = await supabase.auth.getSession();

      if (!authSession?.access_token) {
        throw new Error('Authentication required');
      }

      const { error: functionError } = await supabase.functions.invoke('complete-session', {
        headers: {
          Authorization: `Bearer ${authSession.access_token}`,
        },
        body: {
          sessionId: session.id,
        },
      });

      if (functionError) {
        throw functionError;
      }

      patchSession(session.id, { status: 'completed' });
      setSuccessMessage('Session marked as completed.');
      onDataChange?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark session complete';
      setError(message);
    } finally {
      setActionLoading(null);
    }
  }, [patchSession, onDataChange]);


  const handleMarkNoShowSession = useCallback(async () => {
    if (!sessionToMarkNoShow?.id) return;

    setActionLoading(`no-show-${sessionToMarkNoShow.id}`);
    setSuccessMessage(null);
    setError(null);

    try {
      const supabase = getSupabaseClient();
      const { data: { session: authSession } } = await supabase.auth.getSession();

      if (!authSession?.access_token) {
        throw new Error('Authentication required');
      }

      const { error: functionError } = await supabase.functions.invoke('session-no-show', {
        headers: {
          Authorization: `Bearer ${authSession.access_token}`,
        },
        body: {
          session_id: sessionToMarkNoShow.id,
          notes: noShowNotes.trim() || undefined,
        },
      });

      if (functionError) {
        throw functionError;
      }

      patchSession(sessionToMarkNoShow.id, { status: 'no_show' });
      setSuccessMessage('Session marked as no show.');
      setShowNoShowModal(false);
      setSessionToMarkNoShow(null);
      setNoShowNotes('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark session as no show';
      setError(message);
    } finally {
      setActionLoading(null);
    }
  }, [sessionToMarkNoShow, noShowNotes, patchSession]);

  const loadPackageOffers = async (
    guideUuid: string,
    seekerAuthId: string,
  ): Promise<PackageOffer[]> => {
    const supabase = getSupabaseClient();
    const { data: offerRows, error: offerError } = await supabase
      .from('package_offers')
      .select(
        `id, name, description, duration_months, status, subtotal_cents, total_cents, created_at,
         package_offer_units (id, template_component_id, component_name, component_type, unit_index, status, unit_price_cents, scheduled_session_id, notes, session_length_minutes)`
      )
      .eq('guide_id', guideUuid)
      .eq('seeker_id', seekerAuthId)
      .neq('status', 'withdrawn')
      .order('created_at', { ascending: false });

    if (offerError) throw offerError;
    if (!offerRows || offerRows.length === 0) return [];

    return (offerRows as PackageOfferRow[])
      .map((row) => mapPackageOffer(row, row.package_offer_units ?? []))
      .filter((offer): offer is PackageOffer => offer !== null);
  };

  useEffect(() => {
    if (!isOpen || !seeker?.id) {
      setDetail(null);
      setError(null);
      setLoading(false);
      setGuideId(null);
      setSeekerUserId(null);
      setSeekerHasAccount(false);
      setShowChat(false);
      setIsChatDrawerVisible(false);
      setSuccessMessage(null);
      setShowSendPackageModal(false);
      setShowScheduleUnitModal(false);
      setUnitToSchedule(null);
      setScheduleModalSeekerId(null);
      setShowScheduleSessionModal(false);
      setShowNoShowModal(false);
      setSessionToMarkNoShow(null);
      setNoShowNotes('');
      return;
    }

    let cancelled = false;

    const loadDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const supabase = getSupabaseClient();
        const { data: { user }, error: authError } = await supabase.auth.getUser();

        if (authError) {
          throw authError;
        }

        if (!user) {
          throw new Error('You must be logged in to view seeker details');
        }

        if (!cancelled) {
          setGuideId(user.id);
        }

        let seekerMapping: { seeker_id?: string | null; user_id?: string | null } | null = null;
        let resolvedSeekerUserId: string | null = null;
        let resolvedSeekerRecordId: string | null = null;

        try {
          const { data: mappingRow } = await supabase
            .from('seekers')
            .select('seeker_id, user_id')
            .eq('seeker_id', seeker.id)
            .maybeSingle();

          if (mappingRow) {
            seekerMapping = mappingRow;
          } else if (typeof seeker?.id === 'string') {
            const { data: mappingByUser } = await supabase
              .from('seekers')
              .select('seeker_id, user_id')
              .eq('user_id', seeker.id)
              .maybeSingle();

            if (mappingByUser) {
              seekerMapping = mappingByUser;
            }
          }

          resolvedSeekerUserId = seekerMapping?.user_id ?? null;
          resolvedSeekerRecordId = seekerMapping?.seeker_id ?? (typeof seeker?.id === 'string' ? seeker.id : null);

          if (!cancelled) {
            setSeekerUserId(resolvedSeekerUserId);
            setSeekerHasAccount(!!seekerMapping?.user_id);
          }
        } catch (mappingErr) {
          console.warn('Unable to load seeker mapping for notes', mappingErr);
          if (!cancelled) {
            setSeekerUserId(null);
            setSeekerHasAccount(false);
          }
          resolvedSeekerUserId = null;
          resolvedSeekerRecordId = typeof seeker?.id === 'string' ? seeker.id : null;
        }

        const matchesQuerySeekerId = resolvedSeekerRecordId ?? (typeof seeker?.id === 'string' ? seeker.id : null);
        type NotesQueryResult = { data: NoteRow[] | null | undefined; error: any };

        const sessionsPromise = resolvedSeekerUserId
          ? supabase
              .from('scheduled_sessions')
              .select('id, session_type, session_status, session_date, session_time, start_time, end_time, guide_timezone, created_at, duration_minutes, scheduled_by')
              .eq('guide_id', user.id)
              .eq('seeker_id', resolvedSeekerUserId)
              .order('session_date', { ascending: true })
              .order('session_time', { ascending: true })
          : Promise.resolve({ data: [] as ScheduledSessionRow[], error: null });

        const notesPromise: Promise<NotesQueryResult> = resolvedSeekerUserId
          ? supabase
              .from('guide_notes')
              .select('id, note_text, created_at')
              .eq('guide_id', user.id)
              .eq('seeker_id', resolvedSeekerUserId)
              .order('created_at', { ascending: false }) as Promise<NotesQueryResult>
          : Promise.resolve({ data: [] as NoteRow[], error: null });

        const [matchResult, sessionsResult, notesResult] = await Promise.all([
          matchesQuerySeekerId
            ? supabase
                .from('matches')
                .select('id, seeker_id, guide_id, match_score, reasoning, score_breakdown, created_at')
                .eq('guide_id', user.id)
                .eq('seeker_id', matchesQuerySeekerId)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle()
            : Promise.resolve({ data: null, error: null }),
          sessionsPromise,
          notesPromise
        ]);

        if (matchResult && 'error' in matchResult && matchResult.error) {
          throw matchResult.error;
        }

        const matchRow = matchResult && 'data' in matchResult ? (matchResult.data as MatchesRow | null) : null;
        if (!cancelled) {
          setLatestMatch(matchRow);
        }

        if (sessionsResult.error) {
          throw sessionsResult.error;
        }

        if (notesResult?.error) {
          // Log but continue with match/session data
          console.warn('Failed to load notes', notesResult.error);
        }

        const sessions = (sessionsResult.data as ScheduledSessionRow[] | null | undefined)?.map((row) =>
          mapSessionRow(row, guideTimezone)
        ) ?? [];
        const notes = mapNotes(notesResult?.data);
        let packageOffers: PackageOffer[] = [];
        if (resolvedSeekerUserId) {
          try {
            packageOffers = await loadPackageOffers(user.id, resolvedSeekerUserId);

            // Load reviews for each package offer
            if (packageOffers.length > 0) {
              const reviewsMap: Record<string, any> = {};
              for (const offer of packageOffers) {
                try {
                  const { data: reviewData } = await supabase
                    .from('guide_reviews')
                    .select('*')
                    .eq('offer_id', offer.id)
                    .maybeSingle();

                  if (reviewData) {
                    reviewsMap[offer.id] = reviewData;
                  }
                } catch (reviewErr) {
                  console.warn(`Failed to load review for offer ${offer.id}`, reviewErr);
                }
              }
              if (!cancelled) {
                setPackageReviews(reviewsMap);
              }
            }
          } catch (offerErr) {
            console.warn('Unable to load package offers for seeker', offerErr);
          }
        }

        let profileInfo: { full_name?: string | null; phone?: string | null } | null = null;
        try {
          const seekerProfileUserId = resolvedSeekerUserId;

          if (seekerProfileUserId) {
            const { data: profileRow } = await supabase
              .from('profiles')
              .select('full_name, phone, guide_initiated_changes')
              .eq('id', seekerProfileUserId)
              .maybeSingle();

            if (profileRow) {
              profileInfo = {
                full_name: profileRow.full_name ?? null,
                phone: profileRow.phone ?? null,
              };
              if (!cancelled) {
                setGuideInitiatedChanges(profileRow.guide_initiated_changes ?? 0);
              }
            }
          } else if (seeker.email) {
            const { data: profileRow } = await supabase
              .from('profiles')
              .select('full_name, phone, guide_initiated_changes')
              .eq('email', seeker.email)
              .maybeSingle();
            if (profileRow) {
              profileInfo = {
                full_name: profileRow.full_name ?? null,
                phone: profileRow.phone ?? null,
              };
              if (!cancelled) {
                setGuideInitiatedChanges(profileRow.guide_initiated_changes ?? 0);
              }
            }
          }
        } catch (profileErr) {
          console.warn('Unable to load seeker profile info for detail modal', profileErr);
        }

        const detailIdentifier = resolvedSeekerRecordId ?? (typeof seeker?.id === 'string' ? seeker.id : String(seeker?.id ?? ''));

        const detailData = buildSeekerDetail(
          detailIdentifier,
          profileInfo?.full_name || seeker.name || 'Seeker',
          null,
          profileInfo?.phone ?? seeker.phone ?? null,
          matchRow,
          sessions,
          notes,
          packageOffers,
        );

        if (!cancelled) {
          setDetail(detailData);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load seeker details';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadDetails();

    return () => {
      cancelled = true;
    };
  }, [isOpen, seeker?.id, seeker?.name, refreshCounter]);

  const handleConfirmSession = useCallback(async (session: Session) => {
    if (!session?.id) return;

    setActionLoading(`confirm-${session.id}`);
    setSuccessMessage(null);
    setError(null);
    try {
      await updateSessionStatus(session.id, 'confirmed');
      patchSession(session.id, { status: 'confirmed' });

      // Create Google Calendar event if guide has calendar connected AND event doesn't already exist
      if (session.startsAt && guideId) {
        try {
          const supabase = getSupabaseClient();

          // Check if session already has a calendar event
          const { data: sessionData } = await supabase
            .from('scheduled_sessions')
            .select('google_event_id')
            .eq('id', session.id)
            .single();

          // Only create event if one doesn't exist
          if (!sessionData?.google_event_id) {
            const { data: { session: authSession } } = await supabase.auth.getSession();

            if (authSession?.access_token) {
              // Calculate duration based on session type
              const durationMinutes = COMPONENT_TYPE_DEFAULT_DURATIONS[session.type] ?? 30;

              // Parse start time and calculate end time
              const startDate = new Date(session.startsAt);
              const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

              const seekerName = detail?.name ?? seeker?.name ?? 'Seeker';

              // NEW: Use guide's timezone from session or fall back to hook's timezone
              const calendarTimezone = session.guideTimezone || guideTimezone;

              await supabase.functions.invoke('google-calendar-create-event', {
                headers: {
                  Authorization: `Bearer ${authSession.access_token}`,
                },
                body: {
                  title: `${session.name} - ${seekerName}`,
                  description: `${session.name} session with ${seekerName}`,
                  startISO: startDate.toISOString(),
                  endISO: endDate.toISOString(),
                  timeZone: calendarTimezone, // NEW: Use guide timezone instead of browser timezone
                  attendees: [],
                  sessionId: session.id,
                },
              });
              console.log('Calendar event created successfully');
            }
          } else {
            console.log('Session already has calendar event, skipping creation');
          }
        } catch (calendarErr) {
          // Log but don't fail the confirmation if calendar creation fails
          console.warn('Failed to create calendar event:', calendarErr);
        }
      }

      setSuccessMessage('Session confirmed and added to calendar.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to confirm session';
      setError(message);
    } finally {
      setActionLoading(null);
    }
  }, [patchSession, guideId, detail, seeker]);

  useEffect(() => {
    if (showChat) {
      const raf = requestAnimationFrame(() => setIsChatDrawerVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setIsChatDrawerVisible(false);
    return undefined;
  }, [showChat]);

  useEffect(() => {
    return () => {
      if (closeChatTimeoutRef.current) {
        window.clearTimeout(closeChatTimeoutRef.current);
        closeChatTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCloseChat = () => {
    setIsChatDrawerVisible(false);
    if (closeChatTimeoutRef.current) {
      window.clearTimeout(closeChatTimeoutRef.current);
    }
    closeChatTimeoutRef.current = window.setTimeout(() => {
      setShowChat(false);
    }, 250);
  };

  const handleRescheduleSession = async (session: Session) => {
    setSelectedSession(session);
    setShowRescheduleModal(true);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleStartUtc('');
    setRescheduleAvailability([]);

    // Fetch availability
    if (guideId) {
      setLoadingAvailability(true);
      try {
        const env = (window as any).__ENV__;
        const baseUrl = env?.SUPABASE_URL;

        if (baseUrl) {
          const today = new Date();
          const startDate = today.toISOString().split('T')[0];
          const endDate = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

          const response = await fetch(
            `${baseUrl}/functions/v1/guide-availability?guide_id=${guideId}&start_date=${startDate}&end_date=${endDate}`
          );

          if (response.ok) {
            const data = await response.json();
            setRescheduleAvailability(data.availability || []);
          }
        }
      } catch (err) {
        console.error('Failed to fetch availability for reschedule:', err);
      } finally {
        setLoadingAvailability(false);
      }
    }
  };

  const handleSaveReschedule = async () => {
    if (!selectedSession?.id || !rescheduleDate || !rescheduleTime) return;

    setActionLoading(`reschedule-${selectedSession.id}`);
    setSuccessMessage(null);
    setError(null);
    try {
      // Call the session-reschedule edge function
      // This handles: UTC time computation, notifications, calendar events, and database updates
      await rescheduleSession(selectedSession.id, rescheduleDate, rescheduleTime, rescheduleStartUtc || undefined);

      // Determine the UTC start time for local state update
      // Prefer the explicit UTC value from the availability slot; fall back to constructing one from date+time
      const newStartsAt = rescheduleStartUtc || `${rescheduleDate}T${rescheduleTime}:00Z`;
      const sessionTz = selectedSession.guideTimezone || guideTimezone || null;
      const newDateLabel = formatDateTimeLabel(newStartsAt, sessionTz, guideTimezone, selectedSession.durationMinutes);

      // Update local state to reflect the reschedule
      patchSession(selectedSession.id, {
        startsAt: newStartsAt,
        dateLabel: newDateLabel,
        status: 'scheduled',
      });

      setShowRescheduleModal(false);
      setSelectedSession(null);
      setRescheduleDate('');
      setRescheduleTime('');
      setRescheduleStartUtc('');
      setRescheduleDisplayedMonth(new Date());
      setSuccessMessage('Session rescheduled successfully.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reschedule session';
      setError(message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCloseScheduleUnitModal = () => {
    setShowScheduleUnitModal(false);
    setUnitToSchedule(null);
    setScheduleModalSeekerId(null);
  };

  const handleUnitScheduled = (_sessionId: string, { rescheduled }: { rescheduled: boolean }) => {
    const scheduledName = unitToSchedule?.componentName ?? 'Session';
    setShowScheduleUnitModal(false);
    setUnitToSchedule(null);
    setScheduleModalSeekerId(null);
    setError(null);
    setSuccessMessage(
      rescheduled
        ? `${scheduledName} rescheduled successfully.`
        : `${scheduledName} scheduled successfully.`,
    );
    // Trigger synchronous data reload — React batches these state updates together,
    // so the refresh fires after modal cleanup within the same render cycle
    setRefreshCounter((prev) => prev + 1);
    onDataChange?.();
  };

  const handleSessionScheduled = (_sessionId: string) => {
    setShowScheduleSessionModal(false);
    setRefreshCounter((prev) => prev + 1);
    setError(null);
    setSuccessMessage('Session scheduled successfully.');
  };

  const handleSaveNote = async () => {
    const text = newNote.trim();
    if (!text || !guideId || !seekerUserId) {
      if (!seekerUserId) {
        setError('Unable to save note: missing seeker record');
      }
      return;
    }

    setIsSavingNote(true);
    try {
      const supabase = getSupabaseClient();
      const { data, error: insertError } = await supabase
        .from('guide_notes')
        .insert({
          guide_id: guideId,
          seeker_id: seekerUserId,
          note_text: text,
        })
        .select('id, note_text, created_at')
        .single();

      if (insertError) {
        throw insertError;
      }

      const addedNotes = mapNotes(data ? [data as NoteRow] : []);
      const addedNote = addedNotes[0];

      if (addedNote) {
        setDetail((prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            notes: [addedNote, ...prev.notes],
          };
        });
      }

      setNewNote('');
      setShowAddNote(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save note';
      setError(message);
    } finally {
      setIsSavingNote(false);
    }
  };

  const handlePackageSent = async (offerId: string, pkg: Package) => {
    setShowSendPackageModal(false);
    setError(null);
    setSuccessMessage(`Package offer "${pkg.name}" sent successfully.`);

    // Trigger a full refresh to reload all packages
    setRefreshCounter((prev) => prev + 1);
    onDataChange?.();
  };

  const handleRescindOffer = async (offerId: string, offerName: string) => {
    setRescindingOfferId(offerId);
    setError(null);

    try {
      const env = (window as any).__ENV__ ?? {};
      const supabaseUrl = env.SUPABASE_URL ?? '';

      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/rescind-package-offer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ offer_id: offerId }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to rescind offer');
      }

      setSuccessMessage(`Package offer "${offerName}" has been rescinded.`);
      setShowRescindConfirm(null);

      // Trigger a full refresh to reload all packages
      setRefreshCounter((prev) => prev + 1);
      onDataChange?.();
    } catch (err) {
      console.error('Failed to rescind offer:', err);
      setError(err instanceof Error ? err.message : 'Failed to rescind offer');
    } finally {
      setRescindingOfferId(null);
    }
  };

  const packageOffers = detail?.packageOffers ?? [];
  const packageOffer = detail?.packageOffer ?? null; // Legacy

  const computeOfferStats = (offer: PackageOffer) => {
    const units = offer.units;
    const totalUnits = units.length;
    const fulfilledUnits = units.filter((unit) => unit.status === 'fulfilled').length;
    const scheduledUnits = units.filter((unit) => unit.status === 'scheduled').length;
    const cancelledUnits = units.filter((unit) => unit.status === 'cancelled').length;
    const remainingUnits = Math.max(totalUnits - fulfilledUnits - scheduledUnits - cancelledUnits, 0);
    const progressPercent = totalUnits ? Math.min(100, Math.round((fulfilledUnits / totalUnits) * 100)) : 0;
    const scheduledPercent = totalUnits
      ? Math.min(100, Math.round(((fulfilledUnits + scheduledUnits) / totalUnits) * 100))
      : 0;

    return {
      units,
      totalUnits,
      fulfilledUnits,
      scheduledUnits,
      cancelledUnits,
      remainingUnits,
      progressPercent,
      scheduledPercent,
    };
  };
  const effectiveSeekerId =
    seekerUserId ??
    (typeof detail?.id === 'string' ? detail.id : null) ??
    (typeof seeker?.id === 'string' ? seeker.id : null);

  const renderStatusChip = (label: string, count: number, className: string) => {
    if (count <= 0) return null;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
        {label}
        <span className="text-[11px] font-semibold">{count}</span>
      </span>
    );
  };

  const renderUnitStatusChip = (status: OfferUnitStatus) => {
    const className = UNIT_STATUS_CLASSES[status] ?? 'border border-[#2C2731]/40 bg-[#E8DFBD] text-[#2C2731]';
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
        {formatDisplayLabel(status)}
      </span>
    );
  };

  const getUnitActionLabel = (status: OfferUnitStatus) => {
    if (status === 'unallocated') return 'Schedule';
    if (status === 'scheduled') return 'Reschedule';
    if (status === 'cancelled') return 'Reschedule';
    return null;
  };

  const handleScheduleUnit = (unit: PackageOfferUnit) => {
    if (!seekerHasAccount || !seekerUserId) {
      setError('This seeker needs to create an account before sessions can be scheduled.');
      return;
    }
    const targetSeekerId = seekerUserId;
    setUnitToSchedule(unit);
    setScheduleModalSeekerId(targetSeekerId);
    setShowScheduleUnitModal(true);
    setError(null);
  };

  // Reschedule modal calendar helpers
  const normalizedToday = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const rescheduleDurationMinutes = useMemo(() => {
    if (!selectedSession) {
      return 60; // Default fallback
    }

    // Use the session's actual duration if available
    if (selectedSession.durationMinutes) {
      return selectedSession.durationMinutes;
    }

    // Fallback to type-based duration
    const componentType = selectedSession.type?.toLowerCase() || '';
    if (componentType === 'ceremony' || componentType === 'journey') {
      return 240;
    }
    if (componentType === 'consultation') {
      return 30;
    }
    return 60;
  }, [selectedSession]);

  const formatDateKey = (dateObj: Date) => {
    const year = dateObj.getFullYear();
    const month = `${dateObj.getMonth() + 1}`.padStart(2, '0');
    const day = `${dateObj.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeLabel = (slot: { time: string; start_utc?: string; guide_timezone?: string }) => {
    // Prefer start_utc and convert to guide timezone if available
    if (slot.start_utc && guideTimezone) {
      try {
        const d = new Date(slot.start_utc);
        const time = new Intl.DateTimeFormat('en-US', {
          timeZone: guideTimezone,
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).format(d);
        const tz = new Intl.DateTimeFormat('en-US', {
          timeZone: guideTimezone,
          timeZoneName: 'shortGeneric',
        }).formatToParts(d).find(p => p.type === 'timeZoneName')?.value;
        return tz ? `${time} ${tz}` : time;
      } catch (err) {
        console.error('Error formatting time in guide timezone:', err);
      }
    }

    // Legacy fallback: parse HH:MM time string
    const [hours, minutes] = slot.time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours ?? 0, minutes ?? 0, 0, 0);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const startOfCalendar = useMemo(() => {
    const firstDay = new Date(rescheduleDisplayedMonth.getFullYear(), rescheduleDisplayedMonth.getMonth(), 1);
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - firstDay.getDay());
    return startDate;
  }, [rescheduleDisplayedMonth]);

  const goToPrevMonth = () => {
    setRescheduleDisplayedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setRescheduleDisplayedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const canGoPrevMonth = useMemo(() => {
    const prevMonth = new Date(rescheduleDisplayedMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    // Allow going back only if the previous month is not before the current month
    const currentMonth = new Date(normalizedToday.getFullYear(), normalizedToday.getMonth(), 1);
    const prevMonthStart = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
    return prevMonthStart >= currentMonth;
  }, [rescheduleDisplayedMonth, normalizedToday]);

  const calendarDays = useMemo(() => {
    const days: Array<{
      dateKey: string;
      label: number;
      isPast: boolean;
      hasSlots: boolean;
      isSelected: boolean;
      isCurrentMonth: boolean;
    }> = [];

    for (let i = 0; i < 42; i += 1) {
      const currentDate = new Date(startOfCalendar);
      currentDate.setDate(startOfCalendar.getDate() + i);
      const dateKey = formatDateKey(currentDate);
      const isCurrentMonth = currentDate.getMonth() === rescheduleDisplayedMonth.getMonth();
      const isPast = currentDate < normalizedToday && dateKey !== formatDateKey(normalizedToday);
      const dayAvailability = rescheduleAvailability.find((day) => day.date === dateKey);
      // Check if there are any slots that can fit the full session duration
      const hasSlots = dayAvailability?.timeSlots?.some((slot) =>
        canFitSession(dayAvailability.timeSlots, slot.time, rescheduleDurationMinutes)
      ) ?? false;
      const isSelected = rescheduleDate === dateKey;

      days.push({
        dateKey,
        label: currentDate.getDate(),
        isPast,
        hasSlots,
        isSelected,
        isCurrentMonth,
      });
    }

    return days;
  }, [rescheduleAvailability, normalizedToday, rescheduleDate, startOfCalendar, rescheduleDurationMinutes, rescheduleDisplayedMonth]);

  const selectedDaySlots = useMemo(() => {
    if (!rescheduleDate) return [];
    const daySlots = rescheduleAvailability.find((day) => day.date === rescheduleDate)?.timeSlots ?? [];

    // Filter slots to only show those that can fit the full session duration
    return daySlots.map((slot) => ({
      ...slot,
      available: slot.available && canFitSession(daySlots, slot.time, rescheduleDurationMinutes),
    }));
  }, [rescheduleAvailability, rescheduleDate, rescheduleDurationMinutes]);

  if (!isOpen) return null;

  const displayName = detail?.name ?? seeker?.name ?? 'Seeker';
  const activeDate = detail?.activeDate ?? 'Match pending';
  const matchScore = detail?.matchScore != null ? Math.round(detail.matchScore) : null;
  const packageName = detail?.packageName ?? null;
  const packagePrice = detail?.packagePrice ?? null;
  const sessions = detail?.sessions ?? [];
  const notes = detail?.notes ?? [];
  const stats = detail?.stats;
  const totalRevenue = packageOffers.reduce((sum, offer) => sum + (offer.totalCents / 100), 0) || (packagePrice ?? 0);
  const totalOfferUnits = packageOffers.reduce((sum, offer) => sum + offer.units.length, 0);
  const fulfilledOfferUnits = packageOffers.reduce((sum, offer) =>
    sum + offer.units.filter(u => u.status === 'fulfilled').length, 0
  );
  const hasActiveMatch = Boolean(latestMatch?.id);
  const consultationCompleted = sessions.some(
    (s) => s.type === 'consultation' && (s.status === 'completed' || s.status === 'no_show')
  );
  const hasUpcomingConsultation = sessions.some(
    (s) => s.type === 'consultation' && (s.status === 'scheduled' || s.status === 'confirmed')
  );
  const canScheduleConsultation = consultationCompleted && !hasUpcomingConsultation;

  const openChangeRequestModal = () => {
    setChangeReason('');
    setChangeDetails('');
    setChangeError(null);
    setShowChangeRequestModal(true);
  };

  const closeChangeRequestModal = () => {
    if (isSubmittingChange) return;
    setShowChangeRequestModal(false);
  };

  const handleSubmitChangeRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setChangeError(null);
    if (!changeReason) {
      setChangeError('Select a reason to continue.');
      return;
    }
    if (!latestMatch || !guideId) {
      setChangeError('Active match not found.');
      return;
    }

    setIsSubmittingChange(true);
    const supabase = getSupabaseClient();
    const reasonMeta = GUIDE_CHANGE_REASONS.find(reason => reason.id === changeReason);
    const trimmedDetails = changeDetails.trim();

    try {
      const { error: functionError } = await supabase.functions.invoke('request-match-change', {
        body: {
          seeker_id: latestMatch.seeker_id,
          guide_id: guideId,
          prior_match_id: latestMatch.id,
          initiator: 'guide',
          reason_id: changeReason,
          reason_label: reasonMeta?.label ?? null,
          details: trimmedDetails.length > 0 ? trimmedDetails : null
        }
      });

      if (functionError) {
        throw new Error(functionError.message ?? 'Failed to submit change request.');
      }

      setSuccessMessage('Match change request submitted.');
      setShowChangeRequestModal(false);
      setChangeReason('');
      setChangeDetails('');
      setChangeError(null);
      onDataChange?.();
      onClose();
    } catch (submitErr) {
      const message =
        submitErr instanceof Error ? submitErr.message : 'Failed to submit change request.';
      setChangeError(message);
    } finally {
      setIsSubmittingChange(false);
    }
  };

  const renderSessionStatusBadge = (status: Session['status']) => {
    switch (status) {
      case 'completed':
        return (
          <div className="w-6 h-6 rounded-full border border-[#2C2731] bg-[#2C2731] flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-[#FFFCEF]" />
          </div>
        );
      case 'scheduled':
      case 'confirmed':
        return (
          <div className="w-6 h-6 rounded-full border border-[#2C2731] bg-[#FFFCEF] flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-[#2C2731]/30" />
          </div>
        );
      case 'cancelled':
      case 'no_show':
        return (
          <div className="w-6 h-6 rounded-full border border-[#2C2731]/40 bg-[#F3EBCD] flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-[#2C2731]/30" />
          </div>
        );
      default:
        return (
          <div className="w-6 h-6 rounded-full border border-[#2C2731]/30 bg-[#E8DFBD] flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-[#2C2731]/20" />
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay - hidden on mobile since modal is fullscreen */}
      <div className="absolute inset-0 bg-[#2C2731] bg-opacity-50 hidden sm:block" onClick={onClose} />

      {/* Modal - fullscreen on mobile, centered on larger screens */}
      <div className="fixed inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="bg-[#FFFCEF] sm:rounded-lg border-0 sm:border border-[#2C2731]/40 shadow-xl w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[90vh] overflow-hidden">
          
          {/* Header */}
          <div className="p-4 sm:p-6 border-b border-[#2C2731]/30">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                <div className="flex-shrink-0">
                  {generateAvatar(displayName)}
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-xl font-semibold text-[#2C2731] truncate ph-mask">{displayName}</h1>
                  <p className="text-xs sm:text-sm text-[#2C2731]/70 truncate">{activeDate}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                <button
                  className="bg-[#2C2731] text-[#FFFCEF] px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border border-[#2C2731] hover:bg-[#241F2A]"
                  onClick={() => setShowIntakeModal(true)}
                >
                  <span className="hidden sm:inline">View Intake Form</span>
                  <span className="sm:hidden">Intake</span>
                </button>
                <button
                  onClick={onClose}
                  className="rounded-md border border-[#2C2731]/40 p-2 hover:bg-[#E8DFBD]"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Linked modals */}
          {showIntakeModal && (
            <SeekerIntakeModal
              isOpen={showIntakeModal}
              onClose={() => setShowIntakeModal(false)}
              seekerId={detail?.id}
              seekerEmail={undefined}
            />
          )}
          {showSendPackageModal && (
            <SendPackageModal
              isOpen={showSendPackageModal}
              onClose={() => setShowSendPackageModal(false)}
              onNavigateAway={onClose}
              seekerName={displayName}
              seekerId={effectiveSeekerId}
              onSent={handlePackageSent}
            />
          )}

          {/* Two-column layout - stacks on mobile */}
          <div className="flex flex-col md:flex-row overflow-hidden h-[calc(100vh-80px)] sm:h-[calc(90vh-120px)]">

            {/* Left Column - Main Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
              {loading && (
                <div className="text-sm text-[#2C2731]/70">Loading seeker details…</div>
              )}

              {error && (
                <div className="text-sm text-red-600 bg-[#F3EBCD] border border-red-300 rounded-md p-3">
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="text-sm text-[#2C2731] bg-[#E8DFBD] border border-[#2C2731]/30 rounded-md p-3">
                  {successMessage}
                </div>
              )}
              
              {/* Packages */}
              <div>
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-[#2C2731]">Packages</h3>
                </div>

                {packageOffers.length > 0 ? (
                  <div className="space-y-4">
                    {packageOffers.map((offer) => {
                      const stats = computeOfferStats(offer);
                      const review = packageReviews[offer.id];
                      const allUnitsFulfilled = stats.totalUnits > 0 && stats.fulfilledUnits === stats.totalUnits;

                      return (
                        <div key={offer.id} className="rounded-2xl border border-[#2C2731]/30 bg-[#FFFCEF] p-6 shadow-sm">
                          <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-lg font-semibold text-[#2C2731]">{offer.name}</span>
                                  <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${OFFER_STATUS_BADGE_CLASS[offer.status] ?? 'bg-[#E8DFBD] text-[#2C2731]/80'}`}
                                  >
                                    {formatDisplayLabel(offer.status)}
                                  </span>
                                  {allUnitsFulfilled && (
                                    <span className="inline-flex items-center rounded-full bg-emerald-100 border border-emerald-600 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                                      Completed
                                    </span>
                                  )}
                                </div>
                                {offer.description && (
                                  <p className="max-w-xl text-sm text-[#2C2731]/80">{offer.description}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-2 text-xs text-[#2C2731]/70">
                                  <span className="inline-flex items-center rounded-full bg-[#E8DFBD] px-2 py-1">
                                    {offer.durationMonths ?? '—'} month{offer.durationMonths === 1 ? '' : 's'}
                                  </span>
                                  <span className="inline-flex items-center rounded-full bg-[#E8DFBD] px-2 py-1">
                                    {stats.units.length} unit{stats.units.length === 1 ? '' : 's'}
                                  </span>
                                  <span className="inline-flex items-center rounded-full bg-[#E8DFBD] px-2 py-1">
                                    Sent {new Date(offer.createdAt).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-semibold text-[#2C2731]">
                                  {formatCurrency(offer.totalCents / 100)}
                                </div>
                                <p className="text-xs text-[#2C2731]/70">
                                  Subtotal {formatCurrency(offer.subtotalCents / 100)}
                                </p>
                                {(offer.status === 'draft' || offer.status === 'sent') && (
                                  <div className="mt-2">
                                    {showRescindConfirm === offer.id ? (
                                      <div className="flex flex-col items-end gap-2">
                                        <span className="text-xs text-red-600">Are you sure?</span>
                                        <div className="flex gap-2">
                                          <button
                                            type="button"
                                            onClick={() => setShowRescindConfirm(null)}
                                            disabled={rescindingOfferId === offer.id}
                                            className="rounded-full border border-[#2C2731]/40 px-3 py-1 text-xs font-medium text-[#2C2731] transition hover:bg-[#E8DFBD] disabled:opacity-50"
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleRescindOffer(offer.id, offer.name)}
                                            disabled={rescindingOfferId === offer.id}
                                            className="rounded-full border border-red-500 bg-red-500 px-3 py-1 text-xs font-medium text-white transition hover:bg-red-600 disabled:opacity-50"
                                          >
                                            {rescindingOfferId === offer.id ? 'Rescinding...' : 'Confirm'}
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => setShowRescindConfirm(offer.id)}
                                        className="rounded-full border border-red-400 px-3 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50"
                                      >
                                        Rescind Offer
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {allUnitsFulfilled ? (
                              <div className="rounded-xl border border-[#2C2731]/30 bg-white/60 p-4">
                                <div className="text-sm font-semibold text-[#2C2731] mb-2">
                                  Package Complete
                                </div>
                                <p className="text-sm text-[#2C2731]/70 mb-3">
                                  All {stats.totalUnits} session{stats.totalUnits === 1 ? '' : 's'} fulfilled
                                </p>

                                {review ? (
                                  <div className="rounded-lg border border-[#2C2731]/20 bg-[#FFFCEF] p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-semibold text-[#2C2731]">Seeker Review</span>
                                      <div className="flex items-center gap-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <svg
                                            key={star}
                                            className={`h-3 w-3 ${star <= review.rating ? 'text-yellow-500' : 'text-gray-300'}`}
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                          >
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                          </svg>
                                        ))}
                                      </div>
                                    </div>
                                    {review.title && (
                                      <p className="text-sm font-medium text-[#2C2731] mb-1">{review.title}</p>
                                    )}
                                    {review.comment && (
                                      <p className="text-xs text-[#2C2731]/80">{review.comment}</p>
                                    )}
                                    {review.would_recommend && (
                                      <p className="text-xs text-emerald-700 mt-2">Would recommend</p>
                                    )}
                                    <p className="text-xs text-[#2C2731]/50 mt-2">
                                      {new Date(review.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-xs text-[#2C2731]/60 italic">No review submitted yet</p>
                                )}
                              </div>
                            ) : stats.units.length > 0 && (
                              <>
                                <div className="space-y-2">
                                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[#2C2731]/70">
                                    <span>
                                      {stats.fulfilledUnits} completed · {stats.scheduledUnits} scheduled · {stats.remainingUnits} remaining
                                    </span>
                                    <span>{stats.progressPercent}% complete</span>
                                  </div>
                                  <div className="relative h-2 w-full overflow-hidden rounded-full bg-[#E8DFBD]">
                                    <div
                                      className="absolute inset-y-0 left-0 rounded-full bg-[#2C2731]/30"
                                      style={{ width: `${stats.scheduledPercent}%` }}
                                    />
                                    <div
                                      className="absolute inset-y-0 left-0 rounded-full bg-[#2C2731]"
                                      style={{ width: `${stats.progressPercent}%` }}
                                    />
                                  </div>
                                </div>

                                <div className="rounded-xl border border-[#2C2731]/30 bg-[#E8DFBD] p-4">
                                  <div className="flex items-center justify-between text-sm font-semibold text-[#2C2731]">
                                    <span>Session units</span>
                                    <span className="text-xs font-normal text-[#2C2731]/70">{stats.units.length} total</span>
                                  </div>
                                  <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1">
                                    {stats.units.map((unit) => {
                                      const actionLabel = getUnitActionLabel(unit.status);
                                      const unitAmount = formatCurrency((unit.unitPriceCents ?? 0) / 100);
                                      return (
                                        <div
                                          key={unit.id}
                                          className="flex items-center gap-3 rounded-xl border border-[#2C2731]/30 bg-[#FFFCEF] p-3 shadow-sm"
                                        >
                                          <div className="flex flex-1 flex-col gap-1">
                                            <span className="text-sm font-semibold text-[#2C2731]">{unit.componentName}</span>
                                            <span className="text-xs text-[#2C2731]/70">
                                              Session #{unit.unitIndex} • {unit.durationMinutes ?? 60} min
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-[#2C2731]/70">{unitAmount}</span>
                                            {renderUnitStatusChip(unit.status)}
                                            {actionLabel && (
                                              <button
                                                type="button"
                                                onClick={() => handleScheduleUnit(unit)}
                                                className="inline-flex items-center rounded-full border border-[#2C2731]/40 px-3 py-1 text-xs font-medium text-[#2C2731] transition hover:bg-[#E8DFBD]"
                                              >
                                                {actionLabel}
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : packageName ? (
                  <div className="rounded-2xl border border-[#2C2731]/30 bg-[#FFFCEF] p-5 text-sm text-[#2C2731]/80">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-base font-semibold text-[#2C2731]">{packageName}</p>
                        <p className="text-xs text-[#2C2731]/70">Offer not yet sent</p>
                      </div>
                      {packagePrice != null && (
                        <div className="text-lg font-semibold text-[#2C2731]">{formatCurrency(packagePrice)}</div>
                      )}
                    </div>
                    <p className="mt-3 rounded-lg bg-[#F3EBCD] p-3 text-xs text-[#2C2731]/70">
                      This recommendation hasn't been shared with the seeker yet. Send a package offer to begin tracking progress.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-[#2C2731]/30 bg-[#FFFCEF] p-6 text-center text-sm text-[#2C2731]/70">
                    <div className="text-[#2C2731]/80 mb-1">No packages offered yet</div>
                    <div className="text-xs text-[#2C2731]/60">Send a package to begin the journey</div>
                  </div>
                )}
              </div>

              {/* Upcoming Sessions */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-[#2C2731] mb-3 sm:mb-4">Upcoming Sessions</h3>
                {upcomingSessions.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingSessions.map((session) => {
                      const isConfirmed = session.status === 'confirmed';
                      const isScheduled = session.status === 'scheduled';
                      return (
                        <div key={session.id} className="border border-[#2C2731]/30 rounded-lg p-3 sm:p-4">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <div className="flex-shrink-0">{renderSessionStatusBadge(session.status)}</div>
                              <div className="min-w-0">
                                <div className="font-medium text-[#2C2731] text-sm sm:text-base truncate">{session.name}</div>
                                {session.dateLabel && (
                                  <div className="text-xs sm:text-sm text-[#2C2731]/80 mt-1 truncate">{session.dateLabel}</div>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-[#2C2731] border border-[#2C2731] rounded-md hover:bg-[#F3EBCD] disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => handleRescheduleSession(session)}
                                disabled={!!actionLoading}
                              >
                                {actionLoading === `reschedule-${session.id}` ? 'Rescheduling...' : 'Reschedule'}
                              </button>
                              {/* Only show confirm button if seeker scheduled/rescheduled this session OR scheduled_by is null */}
                              {isScheduled && session.scheduledBy !== 'guide' && (
                                <button
                                  className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-[#2C2731] border border-[#2C2731] rounded-md hover:bg-[#E8DFBD] disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => handleConfirmSession(session)}
                                  disabled={!!actionLoading}
                                >
                                  {actionLoading === `confirm-${session.id}` ? 'Confirming...' : 'Confirm'}
                                </button>
                              )}
                              {isConfirmed && (
                                <>
                                  <button
                                    type="button"
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md border border-[#2C2731] text-[#2C2731] bg-[#FFFCEF] hover:bg-[#F3EBCD] disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => handleJoinWaitingRoom(session)}
                                    disabled={!!actionLoading}
                                  >
                                    Join
                                  </button>
                                  <button
                                    type="button"
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md border border-emerald-600 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => handleCompleteSession(session)}
                                    disabled={!!actionLoading}
                                  >
                                    {actionLoading === `complete-${session.id}` ? 'Completing...' : 'Complete'}
                                  </button>
                                  <button
                                    type="button"
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md border border-amber-600 text-amber-600 hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => {
                                      setSessionToMarkNoShow(session);
                                      setShowNoShowModal(true);
                                      setNoShowNotes('');
                                    }}
                                    disabled={!!actionLoading}
                                  >
                                    No Show
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="border border-[#2C2731]/30 rounded-lg p-4 text-center">
                    <div className="text-[#2C2731]/70 mb-1 text-sm sm:text-base">No upcoming sessions</div>
                    <div className="text-xs sm:text-sm text-[#2C2731]/60">Schedule a session to get started</div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-[#2C2731]">Notes</h3>
                  <button
                    onClick={() => setShowAddNote(!showAddNote)}
                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-[#2C2731] border border-[#2C2731] rounded-md hover:bg-[#F3EBCD] flex items-center"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Add Note</span>
                    <span className="sm:hidden">Add</span>
                  </button>
                </div>

                {showAddNote && (
                  <div className="mb-4 border border-[#2C2731]/30 rounded-lg p-4">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note about this seeker..."
                      className="w-full p-3 border border-[#2C2731]/40 rounded-md text-sm focus:ring-[#2C2731] focus:border-[#2C2731]"
                      rows={3}
                    />
                    <div className="flex justify-end space-x-2 mt-3">
                      <button 
                        onClick={() => {
                          setShowAddNote(false);
                          setNewNote('');
                        }}
                        className="px-3 py-1 text-sm text-[#2C2731]/80 border border-[#2C2731]/40 rounded-md hover:bg-[#F3EBCD]"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveNote}
                        disabled={isSavingNote || newNote.trim().length === 0}
                        className={`px-3 py-1 text-sm rounded-md text-[#FFFCEF] ${
                          isSavingNote || newNote.trim().length === 0
                            ? 'bg-[#2C2731]/30 cursor-not-allowed'
                            : 'bg-[#2C2731] hover:bg-[#241F2A]'
                        }`}
                      >
                        {isSavingNote ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="space-y-3">
                  {notes.length === 0 ? (
                    <div className="text-sm text-[#2C2731]/70 py-4 text-center border border-[#2C2731]/30 rounded-lg">
                      No notes recorded yet
                    </div>
                  ) : (
                    notes.map((note: Note) => (
                      <div key={note.id} className="border border-[#2C2731]/30 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="text-xs text-[#2C2731]/70">{note.date}</div>
                        </div>
                        <p className="text-sm text-[#2C2731] leading-relaxed ph-mask">{note.content}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Right Sidebar - horizontal actions on mobile, vertical sidebar on desktop */}
            <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-[#2C2731]/30 bg-[#F3EBCD] md:bg-[#FFFCEF] p-4 sm:p-6 overflow-y-auto space-y-4 sm:space-y-6 flex-shrink-0">
              {/* Action buttons - horizontal scroll on mobile, vertical on desktop */}
              <div className="flex md:flex-col gap-2 sm:gap-3 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
                <button
                  type="button"
                  disabled={!consultationCompleted}
                  onClick={consultationCompleted ? () => {
                    setSuccessMessage(null);
                    setShowSendPackageModal(true);
                  } : undefined}
                  className={`flex-shrink-0 md:w-full text-left text-xs sm:text-sm py-2 sm:py-3 px-3 sm:px-4 flex items-center rounded-md border whitespace-nowrap ${consultationCompleted ? 'text-[#FFFCEF] border-[#2C2731] bg-[#2C2731] hover:bg-[#241F2A]' : 'border-[#2C2731]/10 bg-[#F3EBCD] text-[#2C2731]/40 cursor-not-allowed opacity-60'}`}
                  title={!consultationCompleted ? 'Send package not available until consultation is completed' : undefined}
                >
                  <span className="mr-2 sm:mr-3 text-base sm:text-lg"><img src='/sendpackage.svg' className={`w-4 h-4 sm:w-auto sm:h-auto${!consultationCompleted ? ' opacity-40' : ''}`} /></span>
                  <span className="hidden sm:inline">Send Package</span>
                  <span className="sm:hidden">Package</span>
                </button>
                <button
                  className="flex-shrink-0 md:w-full text-left text-xs sm:text-sm py-2 sm:py-3 px-3 sm:px-4 flex items-center rounded-md border whitespace-nowrap text-[#2C2731] border-[#2C2731]/30 bg-[#FFFCEF] hover:bg-[#F3EBCD]"
                  onClick={() => setShowChat(true)}
                >
                  <span className="mr-2 sm:mr-3 text-base sm:text-lg">
                    <img src='/openmessages.svg' className="w-4 h-4 sm:w-auto sm:h-auto" />
                  </span>
                  <span className="hidden sm:inline">Open Messages</span>
                  <span className="sm:hidden">Messages</span>
                </button>
                {canScheduleConsultation && !(consultationCompleted && packageOffers.some(o => o.status === 'sent' || o.status === 'accepted')) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!seekerHasAccount) {
                        setError('This seeker needs to create an account before sessions can be scheduled.');
                        return;
                      }
                      setShowScheduleSessionModal(true);
                    }}
                    className="flex-shrink-0 md:w-full text-left text-xs sm:text-sm py-2 sm:py-3 px-3 sm:px-4 flex items-center rounded-md border whitespace-nowrap text-[#FFFCEF] border-[#2C2731] bg-[#2C2731] hover:bg-[#241F2A]"
                  >
                    <span className="mr-2 sm:mr-3 text-base sm:text-lg"><img src='/schedulesession.svg' className="w-4 h-4 sm:w-auto sm:h-auto invert" /></span>
                    <span className="hidden sm:inline">Schedule Consultation</span>
                    <span className="sm:hidden">Consult</span>
                  </button>
                )}
                {consultationCompleted && packageOffers.some(o => o.status === 'sent' || o.status === 'accepted') && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!seekerHasAccount) {
                        setError('This seeker needs to create an account before sessions can be scheduled.');
                        return;
                      }
                      setShowScheduleSessionModal(true);
                    }}
                    className="flex-shrink-0 md:w-full text-left text-xs sm:text-sm py-2 sm:py-3 px-3 sm:px-4 flex items-center rounded-md border whitespace-nowrap text-[#2C2731] border-[#2C2731]/30 bg-[#FFFCEF] hover:bg-[#F3EBCD]"
                  >
                    <span className="mr-2 sm:mr-3 text-base sm:text-lg"><img src='/schedulesession.svg' className="w-4 h-4 sm:w-auto sm:h-auto" /></span>
                    <span className="hidden sm:inline">Schedule Session</span>
                    <span className="sm:hidden">Schedule</span>
                  </button>
                )}
                {packageOffers.some(o => o.status === 'accepted' && o.units.some(u => u.status === 'unallocated')) && (
                  <button
                    type="button"
                    onClick={() => setShowBatchScheduleModal(true)}
                    className="flex-shrink-0 md:w-full text-left text-xs sm:text-sm text-[#2C2731] py-2 sm:py-3 px-3 sm:px-4 flex items-center rounded-md border border-[#2C2731]/30 bg-[#FFFCEF] hover:bg-[#F3EBCD] whitespace-nowrap"
                  >
                    <span className="mr-2 sm:mr-3 text-base sm:text-lg"><img src='/schedulesession.svg' className="w-4 h-4 sm:w-auto sm:h-auto" /></span>
                    <span className="hidden sm:inline">Schedule Multiple</span>
                    <span className="sm:hidden">Batch</span>
                  </button>
                )}
                <button
                  type="button"
                  disabled={!hasActiveMatch}
                  onClick={hasActiveMatch ? openChangeRequestModal : undefined}
                  className={`flex-shrink-0 md:w-full text-left text-xs sm:text-sm py-2 sm:py-3 px-3 sm:px-4 flex items-center rounded-md border whitespace-nowrap ${hasActiveMatch ? 'border-[#B9383A]/60 bg-[#FCECEC] text-[#B9383A] hover:bg-[#FAD9D9]' : 'border-[#2C2731]/10 bg-[#F3EBCD] text-[#2C2731]/50 cursor-not-allowed'}`}
                >
                  <span className="mr-2 sm:mr-3 text-base sm:text-lg" aria-hidden>
                    ⚠️
                  </span>
                  <span className="hidden sm:inline">Request Match Change</span>
                  <span className="sm:hidden">Change</span>
                </button>
              </div>

              {/* Quick Stats - hidden on mobile to save space */}
              <div className="hidden md:block">
                <h4 className="font-semibold text-[#2C2731] mb-3">Quick Stats</h4>
                <div className="bg-[#FFFCEF] border border-[#2C2731]/30 rounded-lg p-4">
                  {stats ? (
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#2C2731]/80">Total Sessions</span>
                        <span className="font-medium text-[#2C2731]">{stats.totalSessions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#2C2731]/80">Completed</span>
                        <span className="font-medium text-[#2C2731]">{stats.completedSessions}</span>
                      </div>
                      {totalOfferUnits > 0 && (
                        <div className="flex justify-between">
                          <span className="text-[#2C2731]/80">Units Completed</span>
                          <span className="font-medium text-[#2C2731]">
                            {fulfilledOfferUnits}/{totalOfferUnits}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-[#2C2731]/80">Total Revenue</span>
                        <span className="font-medium text-[#2C2731]">
                          {formatCurrency(totalRevenue)}
                        </span>
                      </div>
                      <div className="border-t border-[#2C2731]/30 pt-3">
                        <div className="text-[#2C2731]/80 text-xs mb-1">Last Session</div>
                        <div className="font-medium text-[#2C2731] text-xs">
                          {stats.lastSessionLabel !== 'No sessions yet' ? stats.lastSessionLabel : 'Never'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-[#2C2731]/70">No session history yet.</div>
                  )}
                </div>
              </div>

              {/* Match Details - hidden on mobile to save space */}
              {matchScore != null && (
                <div className="hidden md:block">
                  <h4 className="font-semibold text-[#2C2731] mb-3">Match Details</h4>
                  <div className="bg-[#FFFCEF] border border-[#2C2731]/30 rounded-lg p-4">
                    {detail?.matchReasoning && detail.matchReasoning.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs text-[#2C2731]/70 mb-2">Key Factors:</div>
                        {detail.matchReasoning.slice(0, 3).map((reason, index) => (
                          <div key={index} className="text-xs text-[#2C2731] bg-[#F3EBCD] rounded p-2">
                            {reason}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {showChat && detail && (
        <div className="fixed inset-0 z-60 flex justify-end bg-[#2C2731]/30 sm:bg-[#2C2731]/30">
          <div className="h-full w-full sm:max-w-md bg-[#FFFCEF] shadow-2xl transition-transform">
            <GuideChat
              seeker={{
                seekerId: detail.id,
                name: detail.name ?? seeker?.name ?? 'Seeker',
                image: seeker.profileImage ?? undefined,
                status: upcomingSessions.length > 0 ? 'In session window' : 'Available'
              }}
              onBack={() => setShowChat(false)}
            />
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedSession && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-[#2C2731]/80 px-3 py-6">
          <div className="w-full max-w-[520px]">
            <div className="flex max-h-[90vh] flex-col overflow-hidden rounded-3xl border border-[#2C2731] bg-[#FFFCEF] shadow-[0_24px_60px_-32px_rgba(15,8,27,0.45)]">
              <header className="flex h-16 items-center border-b border-[#2C2731]/25 bg-[#2C2731] px-4">
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setSelectedSession(null);
                    setRescheduleDate('');
                    setRescheduleTime('');
                    setRescheduleStartUtc('');
                  }}
                  aria-label="Close"
                >
                  {'\u2190'}
                </button>
                <div className="flex flex-1 justify-center">
                  <h3 className="text-lg font-semibold text-[#FFFCEF]">Reschedule Session</h3>
                </div>
                <div className="w-10" aria-hidden />
              </header>

              <main className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-6">
                <div className="rounded-[32px] border border-[#2C2731]/20 bg-white/90 p-6 shadow-[0_32px_60px_-35px_rgba(32,77,60,0.45)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/80">Current session</p>
                  <h1 className="mt-2 font-semibold text-lg text-[#2C2731]">
                    {selectedSession.name}
                  </h1>
                  {selectedSession.dateLabel && (
                    <p className="mt-1 text-sm text-[#2C2731]/70">{selectedSession.dateLabel}</p>
                  )}
                  <p className="mt-1 text-sm text-[#2C2731]/70">
                    {rescheduleDurationMinutes} minutes
                  </p>
                </div>

                {loadingAvailability ? (
                  <div className="rounded-2xl border border-[#2C2731]/15 bg-white/70 p-5">
                    <div className="h-4 w-2/3 animate-pulse rounded-full bg-[#E8DFBD]" />
                    <div className="mt-4 h-32 animate-pulse rounded-2xl bg-[#E8DFBD]" />
                  </div>
                ) : rescheduleAvailability.length > 0 ? (
                  <>
                    <div className="rounded-2xl border border-[#2C2731]/15 bg-white/85 p-5">
                      <div className="flex items-center justify-between text-[#2C2731]">
                        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/80">Select a date</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={goToPrevMonth}
                            disabled={!canGoPrevMonth}
                            className="rounded-full p-1 text-[#2C2731]/70 transition hover:bg-[#2C2731]/10 disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label="Previous month"
                          >
                            <ChevronLeftIcon className="h-5 w-5" />
                          </button>
                          <span className="min-w-[140px] text-center text-sm font-medium text-[#2C2731]/70">
                            {rescheduleDisplayedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </span>
                          <button
                            type="button"
                            onClick={goToNextMonth}
                            className="rounded-full p-1 text-[#2C2731]/70 transition hover:bg-[#2C2731]/10"
                            aria-label="Next month"
                          >
                            <ChevronRightIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      <p className="mt-1 text-xs text-[#2C2731]/60">All times shown in {guideTimezone}</p>
                      <div className="mt-4 grid grid-cols-7 gap-2 text-center text-xs font-semibold text-[#2C2731]/70">
                        {DAY_LABELS.map((label) => (
                          <div key={label}>{label}</div>
                        ))}
                      </div>
                      <div className="mt-2 grid grid-cols-7 gap-2">
                        {calendarDays.map((day) => (
                          <button
                            key={day.dateKey}
                            onClick={() => (!day.isPast && day.hasSlots ? setRescheduleDate(day.dateKey) : null)}
                            disabled={day.isPast || !day.hasSlots}
                            className={[
                              'h-12 rounded-2xl border text-sm font-semibold transition disabled:cursor-not-allowed',
                              day.isSelected
                                ? 'border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]'
                                : day.hasSlots && day.isCurrentMonth && !day.isPast
                                ? 'border-[#2C2731]/20 bg-[#FFFCEF] text-[#2C2731] hover:border-[#2C2731]'
                                : 'border-transparent bg-transparent text-[#C7C1AD]',
                            ].join(' ')}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {rescheduleDate && (
                      <div className="rounded-2xl border border-[#2C2731]/15 bg-[#FFFCEF] p-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/80">Available times</p>
                        {selectedDaySlots.length ? (
                          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                            {selectedDaySlots.map((slot) => {
                              // Check if this slot is selected by comparing UTC time or legacy time
                              const isSelected = slot.available && (
                                (slot.start_utc && rescheduleStartUtc && slot.start_utc === rescheduleStartUtc) ||
                                (!slot.start_utc && rescheduleTime === slot.time)
                              );
                              return (
                                <button
                                  key={slot.start_utc || slot.time}
                                  onClick={() => {
                                    if (!slot.available) return;

                                    // Store the legacy time for fallback
                                    setRescheduleTime(slot.time);

                                    // If we have start_utc, convert to guide timezone
                                    if (slot.start_utc && guideTimezone) {
                                      setRescheduleStartUtc(slot.start_utc);

                                      try {
                                        const utcDate = new Date(slot.start_utc);
                                        const dateInGuideTz = new Intl.DateTimeFormat('en-CA', {
                                          timeZone: guideTimezone,
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit'
                                        }).format(utcDate);

                                        const timeInGuideTz = new Intl.DateTimeFormat('en-US', {
                                          timeZone: guideTimezone,
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          hour12: false
                                        }).format(utcDate);

                                        setRescheduleDate(dateInGuideTz);
                                        setRescheduleTime(timeInGuideTz);
                                      } catch (err) {
                                        console.error('Error converting UTC to guide timezone:', err);
                                      }
                                    }
                                  }}
                                  disabled={!slot.available}
                                  className={[
                                    'rounded-full border px-4 py-3 text-sm font-semibold transition',
                                    isSelected
                                      ? 'border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]'
                                      : slot.available
                                      ? 'border-[#2C2731]/20 bg-white text-[#2C2731] hover:border-[#2C2731]'
                                      : 'border-[#C7C1AD]/30 bg-[#F5F5F5] text-[#C7C1AD] cursor-not-allowed line-through',
                                  ].join(' ')}
                                >
                                  {formatTimeLabel(slot)}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="mt-4 text-sm text-[#2C2731]/70">No open times for this date.</p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="rounded-2xl border border-[#2C2731]/15 bg-white/80 px-5 py-4 text-sm text-[#2C2731]/70">
                    No reschedule options are currently available.
                  </p>
                )}
              </main>

              <footer className="flex items-center gap-3 border-t border-[#2C2731]/25 bg-gradient-to-t from-[#FFFCEF] via-[#FFFCEF] to-[#FFFCEF]/0 px-6 pb-6 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setSelectedSession(null);
                    setRescheduleDate('');
                    setRescheduleTime('');
                    setRescheduleStartUtc('');
                    setRescheduleDisplayedMonth(new Date());
                  }}
                  className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#2C2731]/40 bg-[#FFFCEF] px-6 py-4 text-lg font-medium text-[#2C2731] transition hover:-translate-y-0.5 hover:border-[#2C2731]"
                >
                  <span className="w-5 text-xl leading-none">{'\u2190'}</span>
                  <span>Cancel</span>
                  <span className="invisible w-5 text-xl leading-none">{'\u2192'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleSaveReschedule}
                  disabled={!rescheduleDate || !rescheduleTime || actionLoading === `reschedule-${selectedSession.id}`}
                  className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#2C2731] bg-[#2C2731] px-6 py-4 text-lg font-semibold text-[#FFFCEF] transition hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50"
                >
                  <span className="invisible w-5 text-xl leading-none">{'\u2190'}</span>
                  <span>{actionLoading === `reschedule-${selectedSession.id}` ? 'Rescheduling…' : 'Reschedule'}</span>
                  <span className="w-5 text-xl leading-none">{'\u2192'}</span>
                </button>
              </footer>
            </div>
          </div>
        </div>
      )}

      {showChangeRequestModal && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-[#2C2731]/70 px-4">
          <form
            onSubmit={handleSubmitChangeRequest}
            className="w-full max-w-md rounded-3xl bg-[#FFFCEF] p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2C2731]">Request Match Change</h3>
              <button
                type="button"
                onClick={closeChangeRequestModal}
                className="rounded-full p-2 text-[#2C2731] hover:bg-[#2C2731]/10"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-[#2C2731]/80 mb-4">
              Share why this match should be updated so our concierge team can review.
            </p>
            {guideInitiatedChanges > 1 && (
              <div className="rounded-2xl border-2 border-[#B24E4E] bg-[#FCEDEA] px-4 py-4 text-sm mb-4">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#B24E4E] text-base font-bold text-white">
                    !
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-[#8C2F2F]">Multiple Match Changes Warning</p>
                    <p className="mt-2 leading-relaxed text-[#8C2F2F]">
                      This seeker has been previously rejected by multiple guides. Submitting a match change request will require manual admin approval and may result in the seeker being removed from the platform. Please carefully consider whether this match can be improved before proceeding.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <fieldset className="space-y-2">
              <legend className="sr-only">Reason</legend>
              {GUIDE_CHANGE_REASONS.map((reason) => {
                const selected = changeReason === reason.id;
                return (
                  <label
                    key={reason.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition ${selected ? 'border-[#2C2731] bg-white' : 'border-[#2C2731]/20 bg-[#FBF2CF]'}`}
                  >
                    <input
                      type="radio"
                      name="guide-change-reason"
                      value={reason.id}
                      checked={selected}
                      onChange={() => setChangeReason(reason.id)}
                      className="sr-only"
                    />
                    <span
                      aria-hidden
                      className={`flex h-4 w-4 items-center justify-center rounded-full border ${selected ? 'border-[#2C2731] bg-[#2C2731]' : 'border-[#2C2731]/40'}`}
                    >
                      {selected ? <span className="h-2 w-2 rounded-full bg-[#FFFCEF]" /> : null}
                    </span>
                    <span className="text-[#2C2731]">{reason.label}</span>
                  </label>
                );
              })}
            </fieldset>
            <div className="mt-4">
              <label htmlFor="guide-change-details" className="text-sm font-medium text-[#2C2731]">
                Additional details <span className="text-[#2C2731]/60">(optional)</span>
              </label>
              <textarea
                id="guide-change-details"
                value={changeDetails}
                onChange={(event) => setChangeDetails(event.target.value)}
                rows={4}
                className="mt-2 w-full rounded-2xl border border-[#2C2731]/30 bg-[#FFFCEF] px-4 py-3 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                placeholder="Include any context that will help us rematch the seeker"
              />
            </div>
            {changeError ? (
              <p className="mt-3 text-sm text-[#B9383A]">{changeError}</p>
            ) : null}
            <div className="mt-6 flex flex-col gap-3">
              <button
                type="submit"
                disabled={isSubmittingChange}
                className={`rounded-full border border-[#2C2731] bg-[#2C2731] px-5 py-3 text-sm font-semibold text-[#FFFCEF] transition ${isSubmittingChange ? 'opacity-70' : 'hover:bg-[#1F1924]'}`}
              >
                {isSubmittingChange ? 'Submitting...' : 'Submit Request'}
              </button>
              <button
                type="button"
                onClick={closeChangeRequestModal}
                className="rounded-full border border-[#2C2731]/30 px-5 py-3 text-sm font-semibold text-[#2C2731]"
                disabled={isSubmittingChange}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* No Show Modal */}
      {showNoShowModal && sessionToMarkNoShow && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-[#2C2731]/80 px-4">
          <div className="w-full max-w-md rounded-3xl bg-[#FFFCEF] p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2C2731]">Mark Session as No Show</h3>
              <button
                type="button"
                onClick={() => {
                  setShowNoShowModal(false);
                  setSessionToMarkNoShow(null);
                  setNoShowNotes('');
                }}
                className="rounded-full p-2 text-[#2C2731] hover:bg-[#2C2731]/10"
                aria-label="Close"
                disabled={!!actionLoading}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-[#2C2731]/80 mb-2">
                Marking this session for <strong>{sessionToMarkNoShow.name}</strong> as a no-show.
              </p>
              {sessionToMarkNoShow.dateLabel && (
                <p className="text-sm text-[#2C2731]/70">
                  Scheduled: {sessionToMarkNoShow.dateLabel}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="no-show-notes" className="text-sm font-medium text-[#2C2731] block mb-2">
                Notes <span className="text-[#2C2731]/60">(optional)</span>
              </label>
              <textarea
                id="no-show-notes"
                value={noShowNotes}
                onChange={(e) => setNoShowNotes(e.target.value)}
                rows={4}
                className="w-full rounded-2xl border border-[#2C2731]/30 bg-white px-4 py-3 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                placeholder="Add any notes about why the seeker didn't show up..."
                disabled={!!actionLoading}
              />
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleMarkNoShowSession}
                disabled={!!actionLoading}
                className={`rounded-full border border-amber-600 bg-amber-600 px-5 py-3 text-sm font-semibold text-white transition ${actionLoading ? 'opacity-70' : 'hover:bg-amber-700'}`}
              >
                {actionLoading === `no-show-${sessionToMarkNoShow.id}` ? 'Marking as No Show...' : 'Mark as No Show'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNoShowModal(false);
                  setSessionToMarkNoShow(null);
                  setNoShowNotes('');
                }}
                className="rounded-full border border-[#2C2731]/30 px-5 py-3 text-sm font-semibold text-[#2C2731] hover:bg-[#F3EBCD]"
                disabled={!!actionLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Render ScheduleUnitModal as a sibling at root level to avoid z-index issues */}
      {showScheduleUnitModal && unitToSchedule && (
        <ScheduleUnitModal
          isOpen={showScheduleUnitModal}
          onClose={handleCloseScheduleUnitModal}
          guideId={guideId}
          seekerId={scheduleModalSeekerId}
          seekerName={detail?.name ?? seeker?.name ?? null}
          seekerEmail={null}
          unit={unitToSchedule}
          onScheduled={handleUnitScheduled}
        />
      )}

      {/* Render ScheduleSessionModal as a sibling at root level to avoid z-index issues */}
      {showScheduleSessionModal && (
        <ScheduleSessionModal
          isOpen={showScheduleSessionModal}
          onClose={() => setShowScheduleSessionModal(false)}
          guideId={guideId}
          seekerId={seekerUserId}
          seekerName={detail?.name ?? seeker?.name ?? null}
          seekerEmail={null}
          packageOffers={packageOffers}
          onScheduled={handleSessionScheduled}
        />
      )}

      {/* Render BatchScheduleModal for scheduling multiple sessions at once */}
      {showBatchScheduleModal && (
        <BatchScheduleModal
          isOpen={showBatchScheduleModal}
          onClose={() => setShowBatchScheduleModal(false)}
          guideId={guideId}
          seekerId={seekerUserId}
          seekerName={detail?.name ?? seeker?.name ?? null}
          seekerEmail={null}
          packageOffers={packageOffers}
          onScheduled={(sessionIds) => {
            // Refresh session list after batch scheduling
            if (sessionIds.length > 0) {
              handleSessionScheduled(sessionIds[0]);
            }
          }}
        />
      )}
    </div>
  );
};

export default SeekerDetailModal;
